import { useId } from 'react';
import { Check } from 'lucide-react';
import {
  CURRENCY,
  PLAN_DETAILS,
  formatPrice,
  getMonthlyEquivalent,
  getPlanPrice,
} from '../utils/planPricing';

const ACCENTS = {
  solo: {
    card: 'border-[color:var(--plan-card-border)]',
    price: 'text-[color:var(--plan-solo-text)]',
    check: 'text-[color:var(--plan-solo-text)]',
  },
  family: {
    card: 'border-[color:var(--plan-family-border)] shadow-[0_8px_24px_rgba(223,167,158,0.25)]',
    price: 'text-[color:var(--plan-family-text)]',
    check: 'text-[color:var(--plan-family-text)]',
  },
};

const BUTTON_BASE =
  'mpg-focus-ring flex min-h-[48px] w-full items-center justify-center rounded-full px-6 text-base font-bold transition-colors disabled:cursor-wait disabled:opacity-70';

const BUTTON_VARIANTS = {
  primary:
    'bg-[var(--plan-button)] text-[color:var(--plan-button-text)] hover:bg-[var(--plan-button-hover)]',
  secondary:
    'border-2 border-[color:var(--plan-solo-text)] bg-white text-[color:var(--plan-solo-text)] hover:bg-[var(--plan-page-bg)]',
};

// The plan screens' button, used inside PlanCard and standalone (the sign-up trial button).
export function PlanButton({ variant = 'primary', busy = false, className = '', children, ...props }) {
  return (
    <button
      type="button"
      disabled={busy}
      aria-busy={busy || undefined}
      className={`${BUTTON_BASE} ${BUTTON_VARIANTS[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

// One plan option, shared by sign-up plan selection (context "signup") and Plan & billing
// (context "billing"); the context only adds "(AUD)" after the price on sign-up.
export function PlanCard({
  plan,
  interval,
  context = 'signup',
  headingLevel = 2,
  badge,
  actionLabel,
  actionVariant = 'primary',
  onAction,
  busy = false,
  hint,
  notice,
  status,
}) {
  const headingId = useId();
  const hintId = useId();
  const details = PLAN_DETAILS[plan];
  const accent = ACCENTS[plan];
  const price = getPlanPrice(plan, interval);
  const isYearly = interval === 'yearly';
  const Heading = headingLevel === 3 ? 'h3' : 'h2';
  const currencySuffix = context === 'signup' ? ` (${CURRENCY})` : '';

  return (
    <article
      aria-labelledby={headingId}
      className={`relative flex min-w-[min(100%,320px)] flex-1 flex-col rounded-3xl border-2 bg-white p-6 sm:p-8 ${accent.card}`}
    >
      {badge && (
        <p className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-[var(--plan-badge-bg)] px-4 py-1 text-sm font-bold text-[color:var(--plan-badge-text)]">
          {badge}
        </p>
      )}

      <Heading id={headingId} className="text-2xl font-bold text-[color:var(--plan-heading-text)]">
        {details.name}
      </Heading>
      <p className="mt-1 text-base text-[color:var(--plan-muted-text)]">{details.tagline}</p>

      <div className="mt-5">
        <p className="flex flex-wrap items-baseline gap-x-2">
          <span className={`text-5xl font-bold leading-tight ${accent.price}`}>{formatPrice(price)}</span>
          <span className="text-lg text-[color:var(--plan-muted-text)]">
            {isYearly ? 'per year' : 'per month'}
            {currencySuffix}
          </span>
        </p>
        <p className="mt-1 text-base text-[color:var(--plan-muted-text)]">
          {isYearly ? `${formatPrice(getMonthlyEquivalent(plan))}/month, billed yearly` : 'Billed monthly'}
        </p>
      </div>

      <ul className="mt-6 flex-1 space-y-3">
        {details.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3 text-base text-[color:var(--plan-heading-text)]">
            <Check className={`mt-0.5 h-5 w-5 shrink-0 ${accent.check}`} strokeWidth={2.5} aria-hidden="true" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-8">
        {notice && (
          <div className="mb-4 rounded-2xl bg-[var(--plan-notice-bg)] p-4 text-base text-[color:var(--plan-notice-text)]">
            {notice}
          </div>
        )}
        <PlanButton
          variant={actionVariant}
          busy={busy}
          onClick={onAction}
          aria-describedby={hint ? hintId : undefined}
        >
          {actionLabel}
        </PlanButton>
        {hint && (
          <p id={hintId} className="mt-3 text-center text-sm text-[color:var(--plan-muted-text)]">
            {hint}
          </p>
        )}
        {/* Always mounted so screen readers announce the message when it appears. */}
        <div role="status" className="text-center text-sm font-medium text-[color:var(--plan-heading-text)]">
          {status && <p className="mt-3">{status}</p>}
        </div>
      </div>
    </article>
  );
}
